// Tracks inline "describe-your-edit" image-edit jobs that are in flight so an
// edit started in one session is RE-ATTACHED (never re-submitted) when the user
// returns to the Edit Image modal / Ads Express KC preview.
//
// The edit itself runs as a durable background job (`image_edit_jobs`) that
// survives navigation and server restarts, but the client previously only
// polled while the modal/page stayed open. If the user closed the modal or left
// the page mid-edit, the job still finished server-side — they just never saw
// the result and had to re-submit (and re-pay). Persisting the in-flight jobId
// here lets the modal/page re-find and resume polling the SAME job on remount,
// so the completed/failed result is surfaced without any duplicate billing.

import type { TextEditDiagnostic } from "@shared/text-edit-diagnostics";
const KEY = "pendingImageEdits";

// Preserve the historical cutoff for legacy entries only. New records must not
// expire a ready result the user has not accepted, or an output awaiting save.
const MAX_AGE_MS = 60 * 60 * 1000;

export interface PendingImageEdit {
  jobId: string;
  // The source image URL the edit was applied to. Used to re-associate a job
  // with the right modal/tile when the user returns (the modal/page reopens
  // keyed on this same source image).
  sourceImageUrl: string;
  // Stable modal/tile identity. It can differ from sourceImageUrl when the
  // user explicitly continues editing a prior result.
  rootImageUrl?: string;
  createdAt: number;
  // Only newly registered, explicitly targeted My Ads jobs may auto-persist.
  target?: { surface: "my-ads"; adId: string; brandId: string };
  delivery?: "pending" | "review" | "background";
  resultImageUrl?: string;
  layeredQuality?: { baselineDiff: number; lowFidelity: boolean; fontFallback: boolean; decompositionId: string };
  error?: string;
  diagnostic?: TextEditDiagnostic | null;
  phase?: string;
}

export function readPendingImageEdits(): PendingImageEdit[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - MAX_AGE_MS;
    return parsed.filter(
      (e: unknown): e is PendingImageEdit =>
        !!e &&
        typeof (e as PendingImageEdit).jobId === "string" &&
        typeof (e as PendingImageEdit).sourceImageUrl === "string" &&
        // Legacy entries retain their old expiry. New lifecycle records stay
        // recoverable until persistence or an explicit discard, even if ready.
        (!!(e as PendingImageEdit).delivery || ((e as PendingImageEdit).createdAt ?? 0) > cutoff),
    );
  } catch {
    return [];
  }
}

function writeAll(entries: PendingImageEdit[]): void {
  try {
    if (entries.length === 0) {
      localStorage.removeItem(KEY);
    } else {
      localStorage.setItem(KEY, JSON.stringify(entries));
    }
  } catch {
    /* ignore quota / availability errors — re-attach is best-effort */
  }
  notify();
}

const listeners = new Set<() => void>();
let revision = 0;
function notify() {
  revision++;
  for (const listener of listeners) listener();
}
export const pendingImageEditRevision = () => revision;
export function subscribePendingImageEdits(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function imageEditOwnerKey(root: string, target?: PendingImageEdit["target"]) {
  return target ? JSON.stringify([target.surface, target.brandId, target.adId, root]) : root;
}

// These locks coordinate owners within this tab, including close-before-response.
const submissions = new Map<string, PendingImageEdit["target"]>();
const modalOwners = new Set<string>();
const saves = new Map<string, Promise<void>>();
export function beginImageEditSubmission(root: string, target?: PendingImageEdit["target"]): boolean {
  if (submissions.has(root)) return false;
  submissions.set(root, target);
  notify();
  return true;
}
export function endImageEditSubmission(root: string) { submissions.delete(root); notify(); }
export function isImageEditSubmitting(root: string) { return submissions.has(root); }
export function readImageEditSubmissions() {
  return [...submissions.values()].filter((target): target is NonNullable<PendingImageEdit["target"]> => !!target);
}
export function setImageEditModalOwner(root: string, open: boolean) {
  if (open) modalOwners.add(root);
  else modalOwners.delete(root);
  notify();
}
export function hasImageEditModalOwner(root: string) { return modalOwners.has(root); }
export function updatePendingImageEdit(jobId: string, patch: Partial<PendingImageEdit>) {
  writeAll(readPendingImageEdits().map(e => e.jobId === jobId ? { ...e, ...patch } : e));
}
export function persistPendingImageEdit(jobId: string, save: () => Promise<void>): Promise<void> {
  const existing = saves.get(jobId);
  if (existing) return existing;
  const promise = Promise.resolve().then(save).then(() => { clearPendingImageEdit(jobId); });
  saves.set(jobId, promise);
  void promise.finally(() => { saves.delete(jobId); notify(); }).catch(() => {});
  return promise;
}
export function isImageEditSaving(jobId: string) { return saves.has(jobId); }

// Record an in-flight edit. New My Ads identities include the original asset,
// since different assets can reuse the same image URL.
export function rememberPendingImageEdit(
  rootImageUrl: string,
  jobId: string,
  sourceImageUrl = rootImageUrl,
  target?: PendingImageEdit["target"],
): void {
  const key = imageEditOwnerKey(rootImageUrl, target);
  const entries = readPendingImageEdits().filter((e) => imageEditOwnerKey(e.rootImageUrl ?? e.sourceImageUrl, e.target) !== key);
  entries.push({ jobId, sourceImageUrl, rootImageUrl, createdAt: Date.now(), delivery: "pending", target });
  writeAll(entries);
}

export function findPendingImageEdit(sourceImageUrl: string, target?: PendingImageEdit["target"]): PendingImageEdit | undefined {
  const entries = readPendingImageEdits();
  if (target) {
    const key = imageEditOwnerKey(sourceImageUrl, target);
    return entries.find(e => imageEditOwnerKey(e.rootImageUrl ?? e.sourceImageUrl, e.target) === key);
  }
  // Untargeted (Express/legacy) consumers must never discover a My Ads job
  // merely because a separate asset happens to reuse its source image.
  return entries.find((e) => !e.target && (e.rootImageUrl ?? e.sourceImageUrl) === sourceImageUrl);
}

// Remove only after successful persistence or explicit discard/supersession.
export function clearPendingImageEdit(jobId: string): void {
  writeAll(readPendingImageEdits().filter((e) => e.jobId !== jobId));
}

// ── Coarse edit-job progress labels ─────────────────────────────────────────
// GET /api/edit-image/:jobId returns an optional `phase` while pending
// (rendering → saving). Shared by every edit polling surface
// so the spinner copy stays consistent. Unknown/absent phases fall back to the
// rendering label (e.g. right after a server restart).
export type ImageEditPhase = "rendering" | "saving" | "checking" | "recovering";

const PHASE_LABELS: Record<ImageEditPhase, string> = {
  recovering: "Recovering existing edit…",
  checking: "Reconnecting — checking existing edit…",
  rendering: "Rendering edit…",
  saving: "Saving…",
};

export function imageEditPhaseLabel(phase: string | null | undefined): string {
  return PHASE_LABELS[(phase as ImageEditPhase) ?? "rendering"] ?? PHASE_LABELS.rendering;
}
